import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, NotebookPen, CalendarCheck, Activity, Quote, ClipboardCheck,
  LineChart as LineIcon, Flame,
} from "lucide-react";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";

export default function AdminClientDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/clients/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load this client."));
  }, [userId]);

  if (err) {
    return (
      <div className="relative min-h-screen px-6 py-8">
        <div className="bps-ambient" />
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/admin")} className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4">
            <ArrowLeft size={16} /> Coach dashboard
          </button>
          <div className="bps-card p-6 text-sm text-[#F87171]" data-testid="client-detail-error">{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
      </div>
    );
  }

  const { user, insight_notes = [], daily_practice = [], pain_scans = [], daily_checkins = [], step_progress = [], surveys = [] } = data;

  // Group insight notes by step
  const notesByStep = {};
  for (const n of insight_notes) {
    if ((n.text || "").trim().length === 0) continue;
    if (!notesByStep[n.step]) notesByStep[n.step] = [];
    notesByStep[n.step].push(n);
  }

  // Group daily practice by step (set of completed days)
  const daysByStep = {};
  for (const d of daily_practice) {
    if (!daysByStep[d.step]) daysByStep[d.step] = new Set();
    daysByStep[d.step].add(d.day);
  }

  const totalDays = daily_practice.length;
  const totalNotes = insight_notes.filter((n) => (n.text || "").trim()).length;
  const lastPain = pain_scans.length ? pain_scans[pain_scans.length - 1].average_intensity : null;
  const firstPain = pain_scans.length ? pain_scans[0].average_intensity : null;
  const painDelta = (lastPain != null && firstPain != null) ? (firstPain - lastPain) : null;
  const weeksDone = step_progress.filter((p) => p.completed_at).length;

  return (
    <div className="relative min-h-screen px-6 py-8">
      <div className="bps-ambient" />
      <div className="max-w-2xl mx-auto pb-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button
            onClick={() => navigate("/admin")}
            className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4 hover:text-white transition-colors"
            data-testid="back-to-admin"
          >
            <ArrowLeft size={16} /> Coach dashboard
          </button>

          <div className="bps-eyebrow">Client journey</div>
          <h1 className="font-display text-3xl font-light tracking-tighter mt-1" data-testid="client-name">{user.name}</h1>
          <div className="text-xs text-[#9AAEC6] mt-1">{user.email} · joined {new Date(user.created_at).toLocaleDateString()}</div>

          {/* Headline stats */}
          <div className="mt-5 grid grid-cols-2 gap-3" data-testid="client-stats">
            <Stat icon={Flame} label="Weeks" value={`${weeksDone}/4`} accent="#22D3EE" />
            <Stat icon={CalendarCheck} label="Daily videos" value={`${totalDays}/28`} accent="#F59E0B" />
            <Stat icon={NotebookPen} label="Reflections" value={totalNotes} accent="#22D3EE" />
            <Stat
              icon={Activity}
              label="Pain shift"
              value={painDelta == null ? "—" : `${painDelta > 0 ? "−" : painDelta < 0 ? "+" : "±"}${Math.abs(painDelta).toFixed(1)}`}
              accent={painDelta == null ? "#9AAEC6" : painDelta > 0 ? "#10B981" : painDelta < 0 ? "#F87171" : "#9AAEC6"}
            />
          </div>

          {/* 7-Day Plan completion per week */}
          <section className="mt-7" data-testid="daily-practice-grid">
            <div className="bps-eyebrow mb-3">7-day practice plans</div>
            <div className="space-y-3">
              {STEPS.map((s) => {
                const set = daysByStep[s.step] || new Set();
                return (
                  <div key={s.step} className="bps-card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full border"
                          style={{ color: s.accent, borderColor: `${s.accent}55` }}
                        >
                          Week {s.step}
                        </span>
                        <span className="text-sm text-white font-display tracking-tight">{s.title}</span>
                      </div>
                      <div className="text-xs text-[#9AAEC6] font-mono" data-testid={`week-${s.step}-count`}>{set.size}/7</div>
                    </div>
                    <div className="grid grid-cols-7 gap-1.5">
                      {[1,2,3,4,5,6,7].map((d) => {
                        const done = set.has(d);
                        return (
                          <div
                            key={d}
                            className="aspect-square rounded-lg flex items-center justify-center font-mono text-[11px] font-semibold transition-all"
                            style={{
                              background: done ? s.accent : "rgba(255,255,255,0.04)",
                              color: done ? "#03101F" : "#7C8FA8",
                              border: `1px solid ${done ? s.accent : "rgba(255,255,255,0.08)"}`,
                            }}
                            data-testid={`week-${s.step}-day-${d}`}
                          >
                            {d}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Insight notes */}
          <section className="mt-7" data-testid="client-notes-section">
            <div className="bps-eyebrow mb-3">Reflections ({totalNotes})</div>
            {totalNotes === 0 ? (
              <div className="bps-card p-6 text-center text-sm text-[#9AAEC6]" data-testid="client-notes-empty">
                No reflections written yet.
              </div>
            ) : (
              <div className="space-y-4">
                {Object.keys(notesByStep).map(Number).sort((a, b) => a - b).map((stepNum) => {
                  const s = STEPS.find((x) => x.step === stepNum);
                  const list = notesByStep[stepNum];
                  return (
                    <div key={stepNum} data-testid={`client-notes-week-${stepNum}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full border"
                          style={{ color: s?.accent, borderColor: `${s?.accent}55` }}
                        >
                          Week {stepNum}
                        </span>
                        <span className="text-xs text-[#9AAEC6]">{s?.title}</span>
                      </div>
                      <div className="space-y-2">
                        {list.map((n, i) => (
                          <div key={`${n.insight_index}-${n.practice_index}`} className="bps-card p-4" data-testid={`client-note-${stepNum}-${i}`}>
                            <div className="flex items-start gap-2 mb-2">
                              <Quote size={12} className="text-[#22D3EE] mt-0.5 flex-shrink-0" />
                              <div className="font-display text-sm text-white leading-snug">
                                {n.insight_text || `Insight ${n.insight_index + 1}`}
                              </div>
                            </div>
                            {n.practice_text && (
                              <p className="text-[11px] text-[#9AAEC6] leading-relaxed italic mb-2 ml-5">
                                Practice {n.practice_index + 1} · {n.practice_text}
                              </p>
                            )}
                            <div className="ml-5 rounded-xl bg-[#03101F]/70 border border-white/10 px-3 py-2 text-sm text-white/90 leading-relaxed whitespace-pre-wrap">
                              {n.text}
                            </div>
                            {n.updated_at && (
                              <div className="mt-1.5 ml-5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C8FA8]">
                                {new Date(n.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Pain history mini-list */}
          {pain_scans.length > 0 && (
            <section className="mt-7" data-testid="client-pain-history">
              <div className="bps-eyebrow mb-3 flex items-center gap-2"><LineIcon size={11} /> Pain history ({pain_scans.length})</div>
              <div className="bps-card p-4 space-y-1.5">
                {pain_scans.slice(-8).reverse().map((s) => (
                  <div key={s.id || s.created_at} className="flex items-center justify-between text-sm">
                    <span className="text-[#9AAEC6] text-xs font-mono">
                      Week {s.step} · {new Date(s.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-white font-medium">{s.average_intensity}/10</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Check-in count */}
          <section className="mt-7" data-testid="client-checkins">
            <div className="bps-eyebrow mb-3 flex items-center gap-2"><ClipboardCheck size={11} /> Daily check-ins</div>
            <div className="bps-card p-4 text-sm">
              <span className="text-white font-display text-lg">{daily_checkins.length}</span>
              <span className="text-[#9AAEC6] ml-2">days logged · {surveys.length} surveys completed</span>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bps-card p-4">
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: accent }} strokeWidth={1.8} />
        <span className="bps-eyebrow" style={{ color: accent }}>{label}</span>
      </div>
      <div className="font-display text-2xl font-light tracking-tight text-white mt-1">{value}</div>
    </div>
  );
}
